import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Flex,
	Text,
	VStack,
	Alert,
	AlertIcon,
	Box,
	Divider,
	Image,
} from 'src/components/ui';
import { MdWarningAmber } from 'react-icons/md';
import CustomModal from 'src/components/common/CustomModal';
import ChannelStatusSection from '../ChannelStatusSection';
import { ICampaignSuccessModalProps } from './types';
import {
	getChannelLinks,
	buildChannelDisplayData,
	sortChannels,
	generateMainMessage,
	has500Error,
} from './utils';

const CHANNEL_INFO_MAP: {
	[key: string]: { name: string; icon: JSX.Element | null };
} = {
	facebook: {
		name: 'Facebook Social',
		icon: (
			<Image
				src="/images/integrations/facebook.png"
				alt="Facebook"
				boxSize="19px"
			/>
		),
	},
	facebookPaid: {
		name: 'Facebook Paid',
		icon: (
			<Image
				src="/images/integrations/facebook.png"
				alt="Facebook Ads"
				boxSize="19px"
			/>
		),
	},
	instagram: {
		name: 'Instagram Social',
		icon: (
			<Image
				src="/images/integrations/instagram.png"
				alt="Instagram"
				boxSize="19px"
			/>
		),
	},
	instagramPaid: {
		name: 'Instagram Paid',
		icon: (
			<Image
				src="/images/integrations/instagram.png"
				alt="Instagram Ads"
				boxSize="19px"
			/>
		),
	},
	google: {
		name: 'Google Search',
		icon: (
			<Image
				src="/images/integrations/google.png"
				alt="Google"
				boxSize="22px"
				width="auto"
			/>
		),
	},
	googlePMAX: {
		name: 'Google PMax',
		icon: (
			<Image
				src="/images/integrations/google.png"
				alt="Google"
				boxSize="22px"
				width="auto"
			/>
		),
	},
	googleDisplay: {
		name: 'Google Display',
		icon: (
			<Image
				src="/images/integrations/google.png"
				alt="Google"
				boxSize="22px"
				width="auto"
			/>
		),
	},
	twitter: {
		name: 'Twitter',
		icon: (
			<Image
				src="/images/integrations/twitter.png"
				alt="Twitter"
				boxSize="19px"
				height="auto"
			/>
		),
	},
	sms: {
		name: 'SMS',
		icon: (
			<Image
				src="/images/integrations/sms.png"
				alt="SMS"
				boxSize="24px"
				height="auto"
			/>
		),
	},
	email: {
		name: 'Email',
		icon: (
			<Image
				src="/images/integrations/email.png"
				alt="Email"
				boxSize="22px"
				height="auto"
			/>
		),
	},
};

const CHANNEL_PRIORITY: { [key: string]: number } = {
	facebook: 1,
	facebookPaid: 2,
	instagram: 3,
	instagramPaid: 4,
	google: 5,
	twitter: 6,
	sms: 7,
	email: 8,
};

const COMING_SOON_CHANNELS = ['sms', 'email', 'twitter', 'pns'];
const ERROR_500_MESSAGE = "We couldn't publish this ad, please try again";
const CREATIVE_PUBLISHED_ERROR_CODE = 'CREATIVE_PUBLISHED';

const CampaignSuccessModal: FC<ICampaignSuccessModalProps> = ({
	title,
	isOpen,
	onClose,
	failedCreatives = [],
	publishedCreatives = [],
	selectedChannels = [],
	fbPageId,
	igName,
}) => {
	const navigate = useNavigate();
	const channelLinks = getChannelLinks(fbPageId, igName);
	const channelDisplayData = buildChannelDisplayData(
		publishedCreatives,
		failedCreatives,
		channelLinks,
		CHANNEL_INFO_MAP,
		CHANNEL_PRIORITY,
		COMING_SOON_CHANNELS,
		CREATIVE_PUBLISHED_ERROR_CODE,
		selectedChannels,
	);
	const sortedChannels = sortChannels(channelDisplayData);
	const channelsWithErrors = sortedChannels.filter(
		(channel) => channel.errorCount > 0,
	);
	const hasErrors = channelsWithErrors.length > 0;
	const is500Error = has500Error(failedCreatives, ERROR_500_MESSAGE);
	const mainMessage = generateMainMessage(
		publishedCreatives,
		failedCreatives,
		is500Error,
		CREATIVE_PUBLISHED_ERROR_CODE,
	);

	const handleSeeCampaigns = () => {
		navigate('/projects/campaigns');
	};

	const handleCreateAnother = () => {
		onClose();
		navigate('/refresh-page', {
			state: { to: '/projects/campaigns/new' },
		});
	};

	const renderFooter = () => {
		if (hasErrors) {
			return (
				<Flex w="full" justify="center">
					<Button onClick={onClose} px={10} w={{ base: 'full', md: 'auto' }}>
						Close
					</Button>
				</Flex>
			);
		}

		return (
			<Flex
				gap={{ base: 3, md: 2 }}
				w="full"
				direction={{ base: 'column', md: 'row' }}
				justify={{ base: 'stretch', md: 'space-between' }}
			>
				<Button
					onClick={handleSeeCampaigns}
					flex={1}
					w={{ base: 'full', md: 'auto' }}
				>
					See campaigns
				</Button>
				<Button
					flex={1}
					colorScheme="secondary"
					onClick={handleCreateAnother}
					w={{ base: 'full', md: 'auto' }}
				>
					Create another campaign
				</Button>
			</Flex>
		);
	};

	const renderErrorAlert = () => {
		if (!hasErrors || is500Error) return null;

		return (
			<Alert
				status="warning"
				mt={2}
				w="full"
				py={2}
				borderRadius="md"
				alignItems="center"
				justifyContent="flex-start"
			>
				<AlertIcon
					as={MdWarningAmber as any}
					boxSize="24px"
					color="orange.500"
					mr={2}
				/>
				<Text fontSize="sm">
					{channelsWithErrors.length}{' '}
					{channelsWithErrors.length > 1
						? 'channels require review'
						: 'channel requires review'}
				</Text>
			</Alert>
		);
	};

	const renderChannelsList = () => {
		return (
			<Box
				w="full"
				maxHeight="60vh"
				overflowY="auto"
				mt={4}
				sx={{
					'&::-webkit-scrollbar': { width: '10px' },
					'&::-webkit-scrollbar-track': {
						background: '#f1f1f1',
						borderRadius: '8px',
					},
					'&::-webkit-scrollbar-thumb': {
						background: '#888',
						borderRadius: '8px',
					},
					'&::-webkit-scrollbar-thumb:hover': { background: '#555' },
					scrollbarGutter: 'stable',
				}}
			>
				<VStack align="start" w="full" spacing={0}>
					{sortedChannels.map((channel, index) => (
						<React.Fragment key={channel.channelId}>
							<ChannelStatusSection
								channelName={channel.channelName}
								channelIcon={channel.channelIcon}
								errors={channel.errors}
								successStatus={channel.successStatus}
								comingSoon={channel.comingSoon}
								linkInfo={channel.linkInfo}
								hasSpecificError={channel.hasSpecificError}
							/>
							{index < sortedChannels.length - 1 && <Divider />}
						</React.Fragment>
					))}
				</VStack>
			</Box>
		);
	};

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={onClose}
			header={
				<Text fontSize="md" fontWeight="bold" textAlign="center">
					{mainMessage}
				</Text>
			}
			footer={renderFooter()}
			isTemplateModal={false}
			size="xl"
		>
			<Flex
				gap={2}
				direction="column"
				alignItems="center"
				textAlign="center"
				w="full"
			>
				{renderErrorAlert()}
				{renderChannelsList()}
			</Flex>
		</CustomModal>
	);
};

export default CampaignSuccessModal;
